import React, { createContext, useCallback, useContext, useState, ReactNode } from "react";
import { ProvisionWizardFormData, defaultProvisionWizardFormData, emptyProductConfig, emptyFirmProfile } from "../types";

interface ProvisionWizardContextProps {
  formData: ProvisionWizardFormData;
  updateFormData: (newData: Partial<ProvisionWizardFormData>) => void;
  updateField: <K extends keyof ProvisionWizardFormData>(field: K, value: ProvisionWizardFormData[K]) => void;
  resetFormData: () => void;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

const ProvisionWizardContext = createContext<ProvisionWizardContextProps | undefined>(undefined);

export function NewClientWizardProvider({ children }: { children: ReactNode }) {
  const [formData, setFormData] = useState<ProvisionWizardFormData>({ ...defaultProvisionWizardFormData });
  const [error, setError] = useState<string | null>(null);

  const updateFormData = useCallback((newData: Partial<ProvisionWizardFormData>) => {
    setFormData((prev) => ({ ...prev, ...newData }));
  }, []);

  const updateField = useCallback(
    <K extends keyof ProvisionWizardFormData>(field: K, value: ProvisionWizardFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const resetFormData = useCallback(() => {
    setFormData({
      config: emptyProductConfig(""),
      firmProfile: { ...emptyFirmProfile },
      accountTypes: [],
      associatedDtcs: [],
    });
    setError(null);
  }, []);

  return (
    <ProvisionWizardContext.Provider
      value={{ formData, updateFormData, updateField, resetFormData, error, setError }}
    >
      {children}
    </ProvisionWizardContext.Provider>
  );
}

export function useNewClientWizardContext(): ProvisionWizardContextProps {
  const context = useContext(ProvisionWizardContext);
  if (!context) {
    throw new Error("useNewClientWizardContext must be used within a NewClientWizardProvider");
  }
  return context;
}
